import React, { useState } from "react";
import { toast } from "sonner";
import { Icon } from "../../";

const IMAGES = {
  copyId:
    "https://downloads.intercomcdn.com/i/o/vyua4cs5/1406702952/33519686387513efa6bd14391518/16019905880333",
  editLocation:
    "https://downloads.intercomcdn.com/i/o/vyua4cs5/1406705189/0b464a95df45f2dc55727f275353/16019918780685",
  externalLocationId:
    "https://downloads.intercomcdn.com/i/o/vyua4cs5/1406706697/06265f1b9ce0ae5a37d068c229bb/16019918825741",
  saveConfig:
    "https://downloads.intercomcdn.com/i/o/vyua4cs5/1406707523/fcdbbc33ef4893850c41a24233ee/16019900730637",
  activateStore:
    "https://downloads.intercomcdn.com/i/o/vyua4cs5/1406708045/1ccd7cdcafa151a73efa6d6fa51e/16019916896781",
};

const SETUP_STEPS = [
  {
    id: 1,
    title: "Get your Client Account ID",
    description:
      'In your Deliverect Dashboard, you can copy the ID by clicking the "ID" button. Paste it in the "Client Account ID" field above.',
    icon: "Key",
    image: IMAGES.copyId,
  },
  {
    id: 2,
    title: "Edit your Location in Deliverect",
    description:
      'In Deliverect, select your location and click "Edit". Open "Ordering Channel Settings" and find "External Location ID".',
    icon: "Settings",
    image: IMAGES.editLocation,
  },
  {
    id: 3,
    title: "Set External Location ID",
    description:
      "Enter the following format in the External Location ID field:",
    code: "project=yourproject&api_key=yourapikey",
    codeNote:
      'Replace "yourproject" with your project name and "yourapikey" with the API key shown above.',
    icon: "Link",
    image: IMAGES.externalLocationId,
  },
  {
    id: 4,
    title: "Save Configuration",
    description:
      'Once the API key is added, click "Save" to apply your settings.',
    icon: "Save",
    image: IMAGES.saveConfig,
  },
  {
    id: 5,
    title: "Activate Store",
    description:
      'Finally, click "Activate" to enable the store on your Ordering platform.',
    icon: "CheckCircle",
    image: IMAGES.activateStore,
  },
];

const DeliverectSetupGuide = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="space-y-4">
      {/* Important Warning Banner */}
      <div className="rounded-lg border border-orange-500/50 bg-orange-900/30 p-4">
        <div className="flex items-start space-x-3">
          <Icon
            name="AlertTriangle"
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-400"
          />
          <div className="flex-1">
            <h4 className="mb-1 text-sm font-semibold text-orange-300">
              Important: Request Your Deliverect Account First
            </h4>
            <p className="mb-3 text-xs text-orange-200/80">
              You must request your Deliverect account before continuing with
              the setup process.
            </p>
            <a
              href="https://www.ordering.co/deliverect-integration-request"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-orange-500"
            >
              <Icon name="ExternalLink" className="h-4 w-4" />
              <span>Request Deliverect Account</span>
            </a>
          </div>
        </div>
      </div>

      {/* Collapsible Setup Guide */}
      <div className="overflow-hidden rounded-lg border border-gray-700 bg-gray-800/50">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-gray-700/30"
        >
          <div className="flex items-center space-x-3">
            <Icon name="BookOpen" className="h-5 w-5 text-blue-400" />
            <span className="font-medium text-white">Setup Guide</span>
            <span className="rounded bg-gray-700 px-2 py-0.5 text-xs text-gray-400">
              5 steps remaining
            </span>
          </div>
          <Icon
            name={isExpanded ? "ChevronUp" : "ChevronDown"}
            className="h-5 w-5 text-gray-400"
          />
        </button>

        {isExpanded && (
          <div className="border-t border-gray-700 px-4 pb-4">
            <div className="space-y-4 pt-4">
              <p className="text-sm text-gray-400">
                Complete these steps to finish setting up your Deliverect
                integration:
              </p>

              <div className="space-y-4">
                {SETUP_STEPS.map((step, index) => (
                  <div
                    key={step.id}
                    className="rounded-lg border border-gray-700/50 bg-gray-900/50 p-3"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-blue-500/30 bg-blue-600/20">
                        <span className="text-xs font-medium text-blue-400">
                          {index + 1}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center space-x-2">
                          <Icon
                            name={step.icon}
                            className="h-4 w-4 text-blue-400"
                          />
                          <h4 className="text-sm font-medium text-white">
                            {step.title}
                          </h4>
                        </div>
                        <p className="text-xs leading-relaxed text-gray-400">
                          {step.description}
                        </p>
                        {step.code && (
                          <div className="mt-2">
                            <div className="flex items-center space-x-2">
                              <code className="flex-1 rounded border border-gray-600 bg-gray-900 px-3 py-2 font-mono text-xs break-all text-green-400">
                                {step.code}
                              </code>
                              <button
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(
                                      step.code,
                                    );
                                    toast.success("Copied to clipboard");
                                  } catch {
                                    toast.error("Failed to copy");
                                  }
                                }}
                                className="flex-shrink-0 rounded p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
                                title="Copy to clipboard"
                              >
                                <Icon name="Copy" className="h-4 w-4" />
                              </button>
                            </div>
                            {step.codeNote && (
                              <p className="mt-1 text-xs text-gray-500 italic">
                                {step.codeNote}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {step.image && (
                      <div className="-mx-3 mt-3">
                        <img
                          src={step.image}
                          alt={step.title}
                          className="h-auto w-full rounded-md border border-gray-600"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliverectSetupGuide;
